import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Collapse,
  ListSubheader,
} from '@mui/material'
import { CheckCircle } from '@mui/icons-material'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { trackEvent } from '@/utils/analytics'
import {
  MILITARY_STATUSES,
  MILITARY_BRANCHES,
  MILITARY_RANK_GROUPS,
} from '@/data/militaryData'

interface EmailSubscribeDialogProps {
  open: boolean
  onClose: () => void
}

// Zod validation schema with conditional military fields
const formSchema = z
  .object({
    firstName: z
      .string()
      .min(1, 'First name is required')
      .max(50, 'First name must be less than 50 characters'),
    lastName: z
      .string()
      .min(1, 'Last name is required')
      .max(50, 'Last name must be less than 50 characters'),
    email: z
      .string()
      .email('Please enter a valid email address')
      .min(5, 'Email is required')
      .max(100, 'Email must be less than 100 characters'),
    phone: z
      .string()
      .regex(/^[0-9\s\-()]*$/, 'Please enter a valid phone number')
      .optional()
      .or(z.literal('')),
    state: z
      .string()
      .length(2, 'Please select your state')
      .regex(/^[A-Z]{2}$/, 'Invalid state code'),
    zipCode: z
      .string()
      .regex(
        /^\d{5}(-\d{4})?$/,
        'ZIP code must be in format 12345 or 12345-6789'
      ),
    isMilitary: z.boolean(),
    militaryStatus: z.string().optional().or(z.literal('')),
    militaryBranch: z.string().optional().or(z.literal('')),
    militaryRank: z.string().optional().or(z.literal('')),
    honeypot: z.string().max(0, 'Spam detected').optional(),
  })
  .superRefine((data, ctx) => {
    if (data.isMilitary) {
      if (!data.militaryStatus) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Military status is required',
          path: ['militaryStatus'],
        })
      }
      if (!data.militaryBranch) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Military branch is required',
          path: ['militaryBranch'],
        })
      }
      if (!data.militaryRank) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Highest rank is required',
          path: ['militaryRank'],
        })
      }
    }
  })

type FormData = z.infer<typeof formSchema>

// US States for dropdown
const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
]

const EmailSubscribeDialog: React.FC<EmailSubscribeDialogProps> = ({
  open,
  onClose,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState<{
    type: 'success' | 'error'
    message: string
  } | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      honeypot: '',
      isMilitary: false,
      militaryStatus: '',
      militaryBranch: '',
      militaryRank: '',
    },
  })

  const isMilitary = watch('isMilitary')

  // Clear military fields when checkbox is unchecked
  useEffect(() => {
    if (!isMilitary) {
      setValue('militaryStatus', '')
      setValue('militaryBranch', '')
      setValue('militaryRank', '')
    }
  }, [isMilitary, setValue])

  const onSubmit = async (data: FormData) => {
    // Check honeypot field - if filled, it's likely spam
    if (data.honeypot && data.honeypot.trim() !== '') {
      setSubmitMessage({
        type: 'error',
        message: 'Spam detected. Please try again.',
      })
      return
    }

    // Track signup initiated
    trackEvent('email_signup_initiated', {
      form_location: 'email_dialog',
      state: data.state,
    })

    setIsSubmitting(true)
    setSubmitMessage(null)

    try {
      // Use FormData to avoid CORS preflight issues
      const formData = new FormData()
      formData.append('firstName', data.firstName)
      formData.append('lastName', data.lastName)
      formData.append('email', data.email)
      formData.append('phone', data.phone || '')
      formData.append('state', data.state)
      formData.append('zipCode', data.zipCode)
      formData.append('isMilitary', data.isMilitary ? 'Yes' : 'No')
      formData.append(
        'militaryStatus',
        data.isMilitary && data.militaryStatus ? data.militaryStatus : ''
      )
      formData.append(
        'militaryBranch',
        data.isMilitary && data.militaryBranch ? data.militaryBranch : ''
      )
      formData.append(
        'militaryRank',
        data.isMilitary && data.militaryRank ? data.militaryRank : ''
      )
      formData.append('userAgent', navigator.userAgent)
      formData.append('timestamp', new Date().toISOString())

      const response = await fetch(
        'https://script.google.com/macros/s/AKfycbwv3kpy-_6dOp1Ckoj-VQpPAQT6ts24dLIIjdQgSkfJ8_2C55_ufBXk2afYBc1yGhoj/exec',
        {
          method: 'POST',
          body: formData,
        }
      )

      if (response.ok) {
        const result = await response.json()
        if (result.success) {
          // Track successful signup
          trackEvent('email_signup_completed', {
            form_location: 'email_dialog',
            state: data.state,
            is_military: data.isMilitary,
          })

          setSubmitMessage({
            type: 'success',
            message:
              "You're all set! 🎉 Thanks for joining the fight for equal parenting rights.",
          })
          reset()
          // Close dialog after successful submission
          setTimeout(() => {
            onClose()
            setSubmitMessage(null)
          }, 4000)
        } else {
          // Track failed signup
          trackEvent('email_signup_failed', {
            form_location: 'email_dialog',
            error_message: result.message || 'Unknown error',
          })

          setSubmitMessage({
            type: 'error',
            message:
              result.message || 'Something went wrong. Please try again.',
          })
        }
      } else {
        throw new Error('Form submission failed')
      }
    } catch (error) {
      // Track failed signup
      trackEvent('email_signup_failed', {
        form_location: 'email_dialog',
        error_message: error instanceof Error ? error.message : 'Network error',
      })

      console.error('Error submitting email:', error)
      setSubmitMessage({
        type: 'error',
        message: 'Network error. Please check your connection and try again.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      onClose()
      setSubmitMessage(null)
      reset()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="email-subscribe-dialog-title"
      aria-describedby="email-subscribe-dialog-description"
    >
      <DialogTitle id="email-subscribe-dialog-title">
        Join the Movement
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent id="email-subscribe-dialog-description">
          <Typography variant="body1" gutterBottom>
            Stay informed about our latest initiatives and how you can make a
            difference. Join thousands of concerned citizens working for change.
          </Typography>

          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                {...register('firstName', {
                  required: 'First name is required',
                  minLength: {
                    value: 2,
                    message: 'First name must be at least 2 characters',
                  },
                  maxLength: {
                    value: 50,
                    message: 'First name cannot exceed 50 characters',
                  },
                })}
                label="First Name *"
                type="text"
                fullWidth
                variant="outlined"
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                disabled={isSubmitting}
                autoComplete="given-name"
                inputProps={{
                  'aria-label': 'First name',
                }}
              />
              <TextField
                {...register('lastName', {
                  required: 'Last name is required',
                  minLength: {
                    value: 2,
                    message: 'Last name must be at least 2 characters',
                  },
                  maxLength: {
                    value: 50,
                    message: 'Last name cannot exceed 50 characters',
                  },
                })}
                label="Last Name *"
                type="text"
                fullWidth
                variant="outlined"
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                disabled={isSubmitting}
                autoComplete="family-name"
                inputProps={{
                  'aria-label': 'Last name',
                }}
              />
            </Box>

            <TextField
              {...register('email', {
                required: 'Email address is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Please enter a valid email address',
                },
              })}
              label="Email Address *"
              type="email"
              fullWidth
              variant="outlined"
              error={!!errors.email}
              helperText={errors.email?.message}
              disabled={isSubmitting}
              autoComplete="email"
              inputProps={{
                'aria-label': 'Email address',
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              {...register('phone', {
                pattern: {
                  value:
                    /^[+]?[1-9][\d]{0,15}$|^\([0-9]{3}\)[\s-]?[0-9]{3}[\s-]?[0-9]{4}$|^[0-9]{3}[\s-]?[0-9]{3}[\s-]?[0-9]{4}$/,
                  message:
                    'Please enter a valid phone number (e.g., (555) 123-4567 or 555-123-4567)',
                },
              })}
              label="Phone Number"
              type="tel"
              fullWidth
              variant="outlined"
              error={!!errors.phone}
              helperText={errors.phone?.message}
              disabled={isSubmitting}
              autoComplete="tel"
              inputProps={{
                'aria-label': 'Phone number',
                placeholder: '(555) 123-4567',
              }}
              sx={{ mb: 2 }}
            />

            <FormControl
              fullWidth
              variant="outlined"
              error={!!errors.state}
              disabled={isSubmitting}
              sx={{ mb: 2 }}
            >
              <InputLabel id="state-select-label">State *</InputLabel>
              <Select
                {...register('state', {
                  required: 'State is required',
                })}
                labelId="state-select-label"
                label="State *"
                defaultValue=""
                inputProps={{
                  'aria-label': 'State',
                }}
              >
                <MenuItem value="">
                  <em>Select your state</em>
                </MenuItem>
                {US_STATES.map(state => (
                  <MenuItem key={state.value} value={state.value}>
                    {state.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.state && (
                <FormHelperText>{errors.state.message}</FormHelperText>
              )}
            </FormControl>

            <TextField
              {...register('zipCode', {
                required: 'ZIP code is required',
                pattern: {
                  value: /^\d{5}(-\d{4})?$/,
                  message:
                    'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)',
                },
                minLength: {
                  value: 5,
                  message: 'ZIP code must be at least 5 digits',
                },
                maxLength: {
                  value: 10,
                  message: 'ZIP code cannot exceed 10 characters',
                },
              })}
              label="ZIP Code *"
              type="text"
              fullWidth
              variant="outlined"
              error={!!errors.zipCode}
              helperText={errors.zipCode?.message}
              disabled={isSubmitting}
              autoComplete="postal-code"
              inputProps={{
                'aria-label': 'ZIP code',
                pattern: '[0-9]{5}(-[0-9]{4})?',
                placeholder: '12345 or 12345-6789',
              }}
            />

            {/* Military affiliation checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  {...register('isMilitary')}
                  checked={isMilitary}
                  disabled={isSubmitting}
                  inputProps={{
                    'aria-label': 'Are you a veteran or active-duty military?',
                  }}
                />
              }
              label="Are you a veteran or active-duty military?"
              sx={{ mt: 1, mb: 1 }}
            />

            {/* Conditional military detail fields */}
            <Collapse in={isMilitary} timeout="auto">
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 1 }}
              >
                {/* Military Status */}
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!errors.militaryStatus}
                  disabled={isSubmitting}
                >
                  <InputLabel id="military-status-label">
                    Military Status *
                  </InputLabel>
                  <Select
                    {...register('militaryStatus')}
                    labelId="military-status-label"
                    label="Military Status *"
                    defaultValue=""
                    inputProps={{
                      'aria-label': 'Military status',
                    }}
                  >
                    <MenuItem value="">
                      <em>Select your status</em>
                    </MenuItem>
                    {MILITARY_STATUSES.map(status => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.militaryStatus && (
                    <FormHelperText>
                      {errors.militaryStatus.message}
                    </FormHelperText>
                  )}
                </FormControl>

                {/* Military Branch */}
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!errors.militaryBranch}
                  disabled={isSubmitting}
                >
                  <InputLabel id="military-branch-label">
                    Primary Military Branch *
                  </InputLabel>
                  <Select
                    {...register('militaryBranch')}
                    labelId="military-branch-label"
                    label="Primary Military Branch *"
                    defaultValue=""
                    inputProps={{
                      'aria-label': 'Primary military branch',
                    }}
                  >
                    <MenuItem value="">
                      <em>Select your branch</em>
                    </MenuItem>
                    {MILITARY_BRANCHES.map(branch => (
                      <MenuItem key={branch.value} value={branch.value}>
                        {branch.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.militaryBranch && (
                    <FormHelperText>
                      {errors.militaryBranch.message}
                    </FormHelperText>
                  )}
                </FormControl>

                {/* Highest Rank Held */}
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!errors.militaryRank}
                  disabled={isSubmitting}
                >
                  <InputLabel id="military-rank-label">
                    Highest Rank Held *
                  </InputLabel>
                  <Select
                    {...register('militaryRank')}
                    labelId="military-rank-label"
                    label="Highest Rank Held *"
                    defaultValue=""
                    inputProps={{
                      'aria-label': 'Highest rank held',
                    }}
                  >
                    <MenuItem value="">
                      <em>Select your rank</em>
                    </MenuItem>
                    {MILITARY_RANK_GROUPS.map(group => [
                      <ListSubheader key={group.group}>
                        {group.group}
                      </ListSubheader>,
                      ...group.ranks.map(rank => (
                        <MenuItem key={rank.value} value={rank.value}>
                          {rank.label}
                        </MenuItem>
                      )),
                    ])}
                  </Select>
                  {errors.militaryRank && (
                    <FormHelperText>
                      {errors.militaryRank.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Collapse>
          </Box>

          {/* Honeypot field - hidden from users but visible to bots */}
          <TextField
            {...register('honeypot')}
            type="text"
            sx={{
              display: 'none',
              visibility: 'hidden',
              position: 'absolute',
              left: '-9999px',
            }}
            tabIndex={-1}
            autoComplete="off"
          />

          {submitMessage && (
            <Alert
              severity={submitMessage.type}
              sx={{ mt: 2 }}
              role="alert"
              icon={
                submitMessage.type === 'success' ? <CheckCircle /> : undefined
              }
            >
              {submitMessage.message}
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting} color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#962029',
              '&:hover': {
                backgroundColor: '#7a1a21',
              },
            }}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default EmailSubscribeDialog
